import React, { useRef, useState, useCallback, memo } from "react";
import { MoreVerticalIcon, Pencil, UserX, ChevronsUpDown, RefreshCcw } from "lucide-react";
import "../../_css/UserManagement.css";
import {
    deleteUser,
    updateUserPermission,
    updateUserProfileImage,
    updateUserField,
    updateUserDepartments,
} from "../../requests.js";
import { useDispatch } from "react-redux";
import { startLoader, stopLoader } from "../../_store/reducers/loaderReducer.js";
import { notifyError, notifyInfo, notifySuccess } from "../../utils/helper.js";
import DeleteUserModal from "../Modals/DeleteUserModal.js";
import { useClickOutsideWithCallback } from "../hooks/useClickOutside.js";

const UserTable = memo(({ filtereddata, startIndex, onUserUpdate, sortField, sortOrder, onSort }) => {
    const [menuOpenIndex, setMenuOpenIndex] = useState(null);
    const [selectedRows, setSelectedRows] = useState(new Set());
    const [editingField, setEditingField] = useState({ id: null, field: null, value: null });
    const [deleteModal, setDeleteModal] = useState({ show: false, userId: null, userName: "" });
    const [activatedUserId, setActivatedUserId] = useState(null);

    const dispatch = useDispatch();
    const dropdownRef = useRef(null);

    const closeMenu = useCallback(() => {
        setMenuOpenIndex(null);
    }, []);

    useClickOutsideWithCallback(dropdownRef, closeMenu);

    const toggleMenu = (idx) => {
        setMenuOpenIndex(prevIndex => prevIndex === idx ? null : idx);
    };

    const handleSort = (key) => {
        if (onSort) {
            if (sortField === key) {
                const newOrder = sortOrder === "asc" ? "desc" : "asc";
                onSort(key, newOrder);
            } else {
                onSort(key, "asc");
            }
        }
    };

    const getSortIcon = (field) => {
        if (sortField === field) {
            return sortOrder === "asc" ? "↑" : "↓";
        }
        return <ChevronsUpDown size={16} />;
    };

    const toggleSelectAll = (e) => {
        if (e.target.checked) {
            const allIds = new Set(filtereddata.map((_, index) => startIndex + index + 1));
            setSelectedRows(allIds);
        } else {
            setSelectedRows(new Set());
        }
    };

    const toggleRowSelection = (rowNumber) => {
        const newSelectedRows = new Set(selectedRows);
        if (newSelectedRows.has(rowNumber)) {
            newSelectedRows.delete(rowNumber);
        } else {
            newSelectedRows.add(rowNumber);
        }
        setSelectedRows(newSelectedRows);
    };

    const isAllSelected = filtereddata.length > 0 && selectedRows.size === filtereddata.length;

    const handlePermissionToggle = async (userId, field, currentValue) => {
        dispatch(startLoader());

        try {
            const res = await updateUserPermission(userId, field, !currentValue);

            // success = true → normal case
            notifySuccess(res?.data?.message || "Permission updated successfully");
            onUserUpdate();

        } catch (error) {
            console.error("Error updating permission:", error);

            // Handle API-returned business error
            if (error?.response) {
                const { status, data } = error.response;

                // Our custom validation error (no company assigned)
                if (status === 400 && data?.message) {
                    notifyInfo(data.message);   // ℹ show info toast
                    return;
                }

                // Other server errors with message
                if (data?.message) {
                    notifyError(data.message);
                    return;
                }
            }
            // Network / unexpected failure
            notifyError("Something went wrong while updating permission");
        } finally {
            dispatch(stopLoader());
        }
    };



    const openDeleteModal = (userId, userName) => {
        setDeleteModal({ show: true, userId, userName });
        setMenuOpenIndex(null);
    };

    const closeDeleteModal = () => {
        setDeleteModal({ show: false, userId: null, userName: "" });
    };

    const handleDeleteConfirm = async () => {
        if (!deleteModal.userId) return;
        dispatch(startLoader());
        try {
            await deleteUser(deleteModal.userId);
            notifySuccess(`User Deleted Successfully`);
            onUserUpdate();
            closeDeleteModal();
        } catch (error) {
            console.error("Error deleting user:", error);
        } finally {
            dispatch(stopLoader());
        }
    };

    const handleProfileImageUpload = async (userId) => {
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = 'image/*';
        fileInput.onchange = async (e) => {
            const file = e.target.files[0];
            if (file) {
                dispatch(startLoader());
                try {
                    await updateUserProfileImage(userId, file);
                    notifySuccess(`Profile image uploaded successfully`);
                    onUserUpdate();
                } catch (error) {
                    console.error("Error updating profile image:", error);
                } finally {
                    dispatch(stopLoader());
                }
            }
        };
        fileInput.click();
        setMenuOpenIndex(null);
    };

    const handleReassignUser = (userId) => {
        setActivatedUserId(userId);
        notifyInfo("User unlocked for editing");
        setMenuOpenIndex(null);
    };

    const startEditing = (id, field, value) => {
        setEditingField({ id, field, value: value || '' });
    };

    const saveEditing = async () => {
        if (editingField.value === null || editingField.value === undefined) return;
        const rowData = filtereddata.find(u => u.id === editingField.id);
        if (rowData) {
            let originalValue = '';
            if (editingField.field === 'name') {
                originalValue = rowData.user?.name;
            } else {
                originalValue = rowData[editingField.field];
            }
            const currentValue = String(editingField.value).trim();
            const prevValue = String(originalValue || '').trim();

            if (currentValue === prevValue) {
                setEditingField({ id: null, field: null, value: null });
                return;
            }
        }
        dispatch(startLoader());
        try {
            let response;
            if (editingField.field === 'department') {
                response = await updateUserDepartments(editingField.id, { department: editingField.value });
            } else {
                response = await updateUserField(editingField.id, editingField.field, editingField.value);
            }
            const successMessage = response?.message || `${editingField.field} updated successfully`;

            if (response?.success) {
                notifySuccess(successMessage);
            } else {
                notifyInfo(successMessage);
            }
            onUserUpdate();
            if (activatedUserId === editingField.id) {
                setActivatedUserId(null);
            }
            setEditingField({ id: null, field: null, value: null });
        } catch (error) {
            console.error(`Error updating ${editingField.field}:`, error);
        } finally {
            dispatch(stopLoader());
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            saveEditing();
        } else if (e.key === 'Escape') {
            setEditingField({ id: null, field: null, value: null });
        }
    };

    const handleEditChange = (e) => {
        setEditingField({ ...editingField, value: e.target.value });
    };

    const handleEditBlur = () => {
        if (editingField.value !== null && editingField.value !== undefined) {
            saveEditing();
        }
    };

    return (
        <>
            <div className="user-table-container custom-scrollbar">
                <div className="user-table-min-width">
                    <table className="user-table text-center align-middle">
                        <thead className="user-table-header">
                            <tr>
                                <th className="user-table-header-cell user-table-header-cell-first">
                                    <div className="d-flex align-items-center">
                                        <input
                                            type="checkbox"
                                            className="form-check-input me-2"
                                            checked={isAllSelected}
                                            onChange={toggleSelectAll}
                                        />
                                        <span>S.L</span>
                                    </div>
                                </th>
                                <th className="user-table-header-cell user-table-header-name" onClick={() => handleSort("name")} style={{ cursor: 'pointer' }}>
                                    Name <span className="sort-icon">{getSortIcon("name")}</span>
                                </th>
                                <th className="user-table-header-cell user-table-header-email" onClick={() => handleSort("email")} style={{ cursor: 'pointer' }}>
                                    Email <span className="sort-icon">{getSortIcon("email")}</span>
                                </th>
                                <th className="user-table-header-cell user-table-header-role">Role</th>
                                <th className="user-table-header-cell user-table-header-company" onClick={() => handleSort("company")} style={{ cursor: 'pointer' }}>
                                    Company <span className="sort-icon">{getSortIcon("company")}</span>
                                </th>
                                <th className="user-table-header-cell user-table-header-department" onClick={() => handleSort("department")} style={{ cursor: 'pointer' }}>
                                    Department <span className="sort-icon">{getSortIcon("department")}</span>
                                </th>
                                <th className="user-table-header-cell user-table-header-token">License</th>
                                <th className="user-table-header-cell user-table-header-token">Total Tokens</th>
                                <th className="user-table-header-cell user-table-header-token">Used Tokens</th>
                                <th className="user-table-header-cell user-table-header-date" onClick={() => handleSort("startDate")} style={{ cursor: 'pointer', minWidth: '150px' }}>
                                    Start date <span className="sort-icon">{getSortIcon("startDate")}</span>
                                </th>
                                <th className="user-table-header-cell user-table-header-date" onClick={() => handleSort("endDate")} style={{ cursor: 'pointer', minWidth: '150px' }}>
                                    End date <span className="sort-icon">{getSortIcon("endDate")}</span>
                                </th>
                                <th className="user-table-header-cell user-table-header-permission">Super Admin</th>
                                <th className="user-table-header-cell user-table-header-teamlead">Teamlead</th>
                                <th className="user-table-header-cell user-table-header-lipsync">Lip Sync</th>
                                <th className="user-table-header-cell user-table-header-edit user-table-header-cell-last">Edit</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtereddata.map((row, index) => {
                                const rowNumber = startIndex + index + 1;
                                const isSelected = selectedRows.has(rowNumber);
                                const isLastRow = index === filtereddata.length - 1;

                                const isSmallList = filtereddata.length < 3;

                                const isInactive = row.status === 'Inactive';
                                const isJustActivated = activatedUserId === row.id;
                                const isEmailEmpty = !row.email || row.email.trim() === '';
                                const isRowLocked = isInactive && !isJustActivated && !isEmailEmpty;

                                let rowClass = "user-table-row";
                                if (isSelected || isJustActivated || isEmailEmpty) {
                                    rowClass = "user-table-row-selected";
                                } else if (isInactive) {
                                    rowClass = "user-table-row-inactive";
                                }

                                return (
                                    <tr
                                        key={row.id || index}
                                        className={`${rowClass} ${isLastRow ? "user-table-cell-last-row" : ""}`}
                                        style={isRowLocked ? { opacity: 0.6, backgroundColor: 'rgba(255, 0, 0, 0.1)' } : {}}
                                    >
                                        <td className="user-table-cell user-table-cell-first">
                                            <div className="d-flex align-items-center">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input me-3"
                                                    checked={isSelected}
                                                    onChange={() => toggleRowSelection(rowNumber)}
                                                    disabled={isRowLocked}
                                                />
                                                <span className="user-table-serial">{String(rowNumber).padStart(2, '0')}</span>
                                            </div>
                                        </td>

                                        <td className="user-table-cell mb-0">
                                            <span className="d-flex align-items-center">
                                                <span className="user-table-avatar me-3 d-flex align-items-center justify-content-center">
                                                    {row.user.profile ? (
                                                        <img src={row.user.profile} alt={row.user.name} width="32" height="32" style={{ objectFit: "cover" }} />
                                                    ) : (
                                                        <span style={{ fontSize: '16px', color: '#6c757d' }}>👤</span>
                                                    )}
                                                </span>
                                                {editingField.id === row.id && editingField.field === 'name' ? (
                                                    <input
                                                        type="text"
                                                        value={editingField.value || ''}
                                                        onChange={handleEditChange}
                                                        onKeyDown={handleKeyPress}
                                                        onBlur={handleEditBlur}
                                                        autoFocus
                                                        className="user-table-edit-input"
                                                        style={{ width: '180px' }}
                                                        disabled={isRowLocked}
                                                    />
                                                ) : (
                                                    <span
                                                        className={`user-table-editable-field d-flex align-items-center gap-1 ${isRowLocked ? 'user-table-field-disabled' : ''}`}
                                                        onClick={isRowLocked ? undefined : () => startEditing(row.id, 'name', row.user.name)}
                                                    >
                                                        {row.user.name || "N/A"}
                                                        {!isRowLocked && <Pencil size={16} strokeWidth={1.5} color="#23B4C3" />}
                                                    </span>
                                                )}
                                            </span>
                                        </td>

                                        {[
                                            { value: row.email || "N/A", field: 'email', editable: true },
                                            { value: row.role || "N/A", field: 'role', editable: true },
                                            { value: row.company || "N/A", field: 'company' },
                                            { value: row.department || "N/A", field: 'department', editable: true },
                                            { value: row.license, field: 'license' },
                                            { value: row.token || 0, field: 'token' },
                                            { value: row.tokenUsage || 0, field: 'tokenUsage' },
                                            { value: row.startDate, field: 'startDate' },
                                            { value: row.endDate, field: 'endDate' },
                                            { value: row.superAdmin, field: 'superAdmin', toggle: true },
                                            { value: row.subAdmin, field: 'subAdmin', toggle: true },
                                            { value: row.lipSync, field: 'lipSync', toggle: true },
                                            { value: row.edit, field: 'edit', menu: true }
                                        ].map((item, idx) => {
                                            const { value, field, editable, toggle, menu } = item;
                                            const isLastColumn = idx === 12;
                                            const isEditing = editingField.id === row.id && editingField.field === field;
                                            const isDateColumn = field === 'startDate' || field === 'endDate';

                                            let displayValue = value;

                                            if (editable) {
                                                if (isEditing) {
                                                    let inputType = 'text';
                                                    let inputWidth = '120px';
                                                    if (field === 'email') { inputType = 'email'; inputWidth = '280px'; }
                                                    else if (isDateColumn) { inputType = 'date'; inputWidth = '150px'; }
                                                    else if (field === 'department') { inputWidth = '180px'; }

                                                    displayValue = (
                                                        <input
                                                            type={inputType}
                                                            value={editingField.value || ''}
                                                            onChange={handleEditChange}
                                                            onKeyDown={handleKeyPress}
                                                            onBlur={handleEditBlur}
                                                            autoFocus
                                                            className="user-table-edit-input"
                                                            style={{ width: inputWidth }}
                                                            disabled={isRowLocked}
                                                        />
                                                    );
                                                } else {
                                                    displayValue = (
                                                        <span
                                                            className={`user-table-editable-field d-flex align-items-center gap-1 ${isRowLocked ? 'user-table-field-disabled' : ''}`}
                                                            onClick={isRowLocked ? undefined : () => startEditing(row.id, field, value)}
                                                        >
                                                            {value || (isDateColumn ? ".." : "Set value")}
                                                            {!isRowLocked && <Pencil size={16} strokeWidth={1.5} color="#23B4C3" />}
                                                        </span>
                                                    );
                                                }
                                            } else if (toggle) {
                                                displayValue = (
                                                    <div className="d-flex justify-content-center align-items-center">
                                                        <label className="switch">
                                                            <input
                                                                type="checkbox"
                                                                checked={value || false}
                                                                onChange={() => handlePermissionToggle(row.id, field, value)}
                                                                disabled={isRowLocked}
                                                            />
                                                            <span className="slider round"></span>
                                                        </label>
                                                    </div>
                                                );
                                            } else if (menu) {
                                                displayValue = (
                                                    <div className="user-table-menu-container d-flex justify-content-center">
                                                        <div className="user-table-menu-icon" onClick={() => toggleMenu(index)}>
                                                            <MoreVerticalIcon size={20} color='#6B7280' />
                                                        </div>
                                                        {menuOpenIndex === index && (
                                                            <div
                                                                className="user-table-dropdown-menu"
                                                                ref={dropdownRef}
                                                                style={{
                                                                    // Open downwards (top: 0) if list is small, to flow into spacer
                                                                    ...(isLastRow && !isSmallList ? { bottom: "0px" } : { top: "0px" }),
                                                                    right: "55px"
                                                                }}
                                                            >
                                                                {isRowLocked ? (
                                                                    <div className="user-table-dropdown-item" onClick={() => handleReassignUser(row.id)}>
                                                                        <RefreshCcw size={18} className="me-2" />
                                                                        Reassign
                                                                    </div>
                                                                ) : (
                                                                    <>
                                                                        <div className="user-table-dropdown-item" onClick={() => handleProfileImageUpload(row.id)}>
                                                                            Add Profile Image
                                                                        </div>
                                                                        <div className="user-table-dropdown-item user-table-dropdown-item-danger" onClick={() => openDeleteModal(row.id, row.user.name)}>
                                                                            <UserX size={18} className="me-2" />
                                                                            Delete User
                                                                        </div>
                                                                    </>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            } else {
                                                displayValue = (
                                                    <span className={`user-table-cell-value ${isRowLocked ? 'user-table-field-disabled' : ''}`}>
                                                        {/* {field === 'token' && value ? `${value.slice(0, 8)}` : value} */}
                                                        {value}
                                                    </span>
                                                );
                                            }

                                            return (
                                                <td
                                                    key={idx}
                                                    className={`user-table-cell mb-0 ${isLastColumn ? "user-table-cell-sticky" : ""} ${isLastRow && isLastColumn ? "user-table-cell-last" : ""}`}
                                                    style={isDateColumn ? { whiteSpace: 'nowrap', minWidth: '150px' } : {}}
                                                >
                                                    {displayValue}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                {/* Spacer to force vertical scroll when menu is open on small lists */}
                {menuOpenIndex !== null && filtereddata.length < 4 && (
                    <div style={{ height: '150px', width: '100%' }} />
                )}
            </div>

            <DeleteUserModal
                show={deleteModal.show}
                onHide={closeDeleteModal}
                onSubmit={handleDeleteConfirm}
            />
        </>
    );
});

export default UserTable;